import React, { useState, useRef, useEffect } from 'react';
import { useFinance } from '../contexts/FinanceContext';

function Settings() {
  const { state, setCurrency, exportData, importData, resetData } = useFinance();
  const [selectedCurrency, setSelectedCurrency] = useState(state.currency);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [storageUsage, setStorageUsage] = useState({ used: 0, percent: 0 });
  const fileInputRef = useRef(null);

  const currencies = [
    { symbol: '₹', name: 'Indian Rupee' },
    { symbol: '$', name: 'US Dollar' },
    { symbol: '€', name: 'Euro' },
    { symbol: '£', name: 'British Pound' },
    { symbol: '¥', name: 'Japanese Yen' },
    { symbol: '₿', name: 'Bitcoin' },
  ];

  useEffect(() => {
    // Calculate LocalStorage Usage
    const calculateStorage = () => {
        let total = 0;
        for (let x in localStorage) {
            if (Object.prototype.hasOwnProperty.call(localStorage, x)) {
                total += (localStorage[x].length * 2);
            }
        }
        // Approximate limit ~5MB = 5 * 1024 * 1024 bytes
        const limit = 5 * 1024 * 1024;
        const usedKB = (total / 1024).toFixed(2);
        const percent = Math.min((total / limit) * 100, 100).toFixed(1);
        setStorageUsage({ used: usedKB, percent });
    };
    calculateStorage();
  }, [state]); // Recalculate when state changes

  const handleSaveCurrency = () => {
    setCurrency(selectedCurrency);
    alert('Currency updated!');
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const result = await importData(file);
      if (result.success) {
        alert('Data imported successfully!');
        window.location.reload();
      } else {
        alert(`Import failed: ${result.error}`);
      }
      e.target.value = '';
    }
  };

  const handleReset = () => {
    resetData();
    alert('All data has been reset!');
    window.location.reload();
  };

  const stats = {
    transactions: state.transactions?.length || 0,
    people: state.people?.length || 0,
    lendings: state.lendings?.length || 0,
    borrowings: state.borrowings?.length || 0,
    categories: state.categories?.length || 0,
    tags: state.tags?.length || 0,
    modes: state.modes?.length || 0,
    recurring: state.recurringTransactions?.length || 0,
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Settings</h2>
        <p className="text-slate-400 mt-1">Manage your preferences and data</p>
      </div>

      {/* Intro Tip */}
      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-2xl p-6 flex flex-col sm:flex-row gap-4 items-center">
          <span className="text-3xl">💡</span>
          <div>
              <p className="text-indigo-200 font-medium">Tip: Want to set an initial balance?</p>
              <p className="text-slate-400 text-sm">Simply add an "Income" transaction for each account/mode with the description "Opening Balance".</p>
          </div>
      </div>
      
      {/* Storage Indicator */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
          <div className="flex justify-between items-end mb-2">
            <h3 className="text-lg font-semibold text-white">Storage Usage</h3>
            <span className={`text-sm font-medium ${storageUsage.percent > 80 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {storageUsage.used} KB used ({storageUsage.percent}%)
            </span>
          </div>
          <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${storageUsage.percent > 80 ? 'bg-rose-500' : 'bg-emerald-500'}`} 
                style={{ width: `${storageUsage.percent}%` }}
              ></div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Data is stored locally in your browser. {storageUsage.percent > 90 && "⚠️ Running low on space, consider backing up and resetting."}</p>
      </div>

      {/* Currency */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">💱 Base Currency</h3>
        <p className="text-slate-400 text-sm mb-4">This currency is used for your total net worth. Individual modes can have their own currencies.</p>
        <div className="flex gap-3">
          <div className="flex-1 grid grid-cols-3 sm:grid-cols-6 gap-2">
            {currencies.map((c) => (
              <button
                key={c.symbol}
                onClick={() => setSelectedCurrency(c.symbol)}
                className={`p-3 rounded-xl border-2 transition-all ${
                  selectedCurrency === c.symbol
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <span className="text-2xl text-white">{c.symbol}</span>
              </button>
            ))}
          </div>
          <button
            onClick={handleSaveCurrency}
            className="px-6 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-500 transition-colors self-start"
          >
            Save
          </button>
        </div>
      </div>

      {/* Data Statistics */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">📊 Data Statistics</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          {Object.entries(stats).map(([key, value]) => (
            <div key={key} className="bg-slate-700/50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-white">{value}</p>
              <p className="text-xs text-slate-400 capitalize">{key}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Backup & Restore */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">💾 Backup & Restore</h3>
        <p className="text-slate-400 text-sm mb-4">Export your data as a JSON file or import a previous backup.</p>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={exportData}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export Backup
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".json"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import Backup
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-rose-950/30 border border-rose-500/30 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-rose-400 mb-4">⚠️ Danger Zone</h3>
        <p className="text-slate-400 text-sm mb-4">Permanently delete all your data. This action cannot be undone.</p>
        
        {!showConfirmReset ? (
          <button
            onClick={() => setShowConfirmReset(true)}
            className="px-6 py-3 rounded-xl bg-rose-600 text-white font-medium hover:bg-rose-500 transition-colors"
          >
            Reset All Data
          </button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleReset}
              className="px-6 py-3 rounded-xl bg-rose-600 text-white font-medium hover:bg-rose-500 transition-colors"
            >
              ⚠️ Yes, Delete Everything
            </button>
            <button
              onClick={() => setShowConfirmReset(false)}
              className="px-6 py-3 rounded-xl bg-slate-700 text-slate-300 font-medium hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* About */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 mb-4">
          <span className="text-3xl">💰</span>
        </div>
        <h3 className="text-xl font-bold text-white">FinanceTracker</h3>
        <p className="text-slate-400 mt-1">Version 1.0.0</p>
        <p className="text-slate-500 text-sm mt-4">Your personal finance companion. Track spending, lending, borrowing, and more – all stored locally in your browser.</p>
      </div>
    </div>
  );
}

export default Settings;
